import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';

import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';

import { SvgIconComponent } from 'app/shared/components/svg-icon/svg-icon.component';
import { ButtonComponent } from 'app/shared/components/button/button.component';
import { InputComponent } from 'app/shared/components/input/input.component';

import { IMessage } from 'app/interfaces/conversations.interfaces';
import { ISvgConfig } from 'app/shared/interfaces/svg.interfaces';
import { IButtonConfig } from 'app/shared/interfaces/button.interfaces';
import { IInputCustomConfig } from 'app/shared/interfaces/input.interfaces';
import { IFeedbackPayload } from 'app/interfaces/feedback.intefaces';

import {
  EFeedbackReportOptions,
  EFeedbackThumbsDownOptions,
  EFeedbackType
} from 'app/enums/feedback.enums';
import { ESvgTypes } from 'app/shared/enums/svg.enums';
import { EButtonSizes, EButtonTypes } from 'app/shared/enums/button.enums';
import { EInputCustomType, EInputSupportMessage, EInputUsage } from 'app/shared/enums/input.enums';

@Component({
  selector: 'stxt-feedback-modal',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, SvgIconComponent, ButtonComponent, InputComponent],
  templateUrl: './feedback-modal.component.html',
  styleUrl: './feedback-modal.component.scss'
})
export class FeedbackModalComponent implements OnInit {
  message: IMessage;
  feedbackType: EFeedbackType;
  public selectedOptions: string[] = [];
  public closeSvg: ISvgConfig = { fill: ESvgTypes.Tonal, name: 'close' };
  public cancelButton: IButtonConfig = {
    text: 'Cancel',
    fill: EButtonTypes.SecondaryDark,
    buttonSize: EButtonSizes.Small
  };
  public submitButton: IButtonConfig = {
    text: 'Submit',
    fill: EButtonTypes.SecondaryFilled,
    buttonSize: EButtonSizes.Small
  };
  public feedbackInput: IInputCustomConfig = {
    placeholder: 'Type your feedback here',
    supportingText: {
      text: EInputSupportMessage.FieldRequired
    },
    type: EInputCustomType.Textarea,
    usageCase: EInputUsage.Modal
  };
  public reportSvg: ISvgConfig = { fill: ESvgTypes.None, name: 'report' };
  public feedbackTypeSvg: ISvgConfig;
  public feedbackTitle: string;
  public feedbackInputForm: FormGroup = new FormGroup({
    feedback: new FormControl('')
  });
  public hintConfiguration: {
    textLeft: string;
    textRight: string;
    svg: ISvgConfig;
  } = {
    textLeft: '',
    textRight: '',
    svg: { fill: ESvgTypes.None, name: '' }
  };
  public EFeedbackType = EFeedbackType;
  public feedbackThumbsDownOptions: EFeedbackThumbsDownOptions[] = Object.values(
    EFeedbackThumbsDownOptions
  );
  public feedbackReportOptions: EFeedbackReportOptions[] = Object.values(EFeedbackReportOptions);
  public scoreValue: number;

  constructor(private readonly modal: NgbActiveModal) {}

  ngOnInit(): void {
    this.initializeFeedbackConfiguration();
    this.setScoreValue();
  }

  closeModal(): void {
    this.modal.dismiss();
  }

  selectOption(option: string): void {
    const index = this.selectedOptions.indexOf(option);
    if (index > -1) {
      this.selectedOptions.splice(index, 1);
    } else {
      this.selectedOptions.push(option);
    }
  }

  isOptionSelected(option: string): boolean {
    return this.selectedOptions.includes(option);
  }

  submitFeedback(): void {
    const feedback: IFeedbackPayload = {
      score: this.scoreValue,
      reason: this.selectedOptions.join(', '),
      comment: this.feedbackInputForm.value.feedback
    };
    this.modal.close(feedback);
  }

  initializeFeedbackConfiguration(): void {
    switch (this.feedbackType) {
      case EFeedbackType.Report:
        this.feedbackTitle = 'What issues do you notice?';
        this.setHintConfiguration('Just not good?', "Mark as 'Bad' instead", 'thumbs_down');
        break;

      case EFeedbackType.ThumbsDown:
        this.feedbackTitle = 'What could have been better?';
        this.setHintConfiguration('Not just low quality?', 'Report instead', 'report');
        break;

      case EFeedbackType.Regenerate:
        this.feedbackTitle = 'Get another response';
        break;

      default:
        this.feedbackTitle = '';
        this.hintConfiguration = {
          textLeft: '',
          textRight: '',
          svg: { fill: ESvgTypes.None, name: '' }
        };
    }
  }

  getCurrentFeedbackOptions(): string[] {
    switch (this.feedbackType) {
      case EFeedbackType.Report:
        return this.feedbackReportOptions;
      case EFeedbackType.ThumbsDown:
      case EFeedbackType.Regenerate:
        return this.feedbackThumbsDownOptions;
      default:
        return [];
    }
  }

  setHintConfiguration(textLeft: string, textRight: string, svg: string): void {
    this.hintConfiguration.textLeft = textLeft;
    this.hintConfiguration.textRight = textRight;
    this.hintConfiguration.svg = { fill: ESvgTypes.None, name: svg };
  }

  switchFeedbackType(): void {
    this.feedbackType =
      this.feedbackType === EFeedbackType.Report ? EFeedbackType.ThumbsDown : EFeedbackType.Report;
    this.initializeFeedbackConfiguration();
    this.feedbackTypeSvg = { fill: ESvgTypes.None, name: this.feedbackType };
  }

  setScoreValue(): void {
    this.scoreValue = this.feedbackType === EFeedbackType.ThumbsUp ? 1 : -1;
  }
}
